import { useLayoutEffect, type RefObject } from 'react';
import { useMinWidth } from './use-min-width';

/**
 * 고정 열·고정 헤더를 켜는 **최소 뷰포트 폭**(px). `0` = 전 breakpoint 켬.
 *
 * 🧪 **지금은 폰까지 켜 두고 체감을 보는 중**(0). 원래 후보는 **768**(태블릿 세로 — 폰만 제외)이었고, 근거는
 * 아래. 실험이 끝나면 둘 중 하나로 확정할 것.
 *
 * - 앱 전역 경계(`useIsCompact`, 1024)는 안 쓴다 — 그건 「인라인 필터를 가로로 펼 폭이 되는가」의 값이라
 *   태블릿을 compact 로 친다. 표 고정은 판단 축이 다르다.
 * - 태블릿(768~1024)은 **가로로 밀어야 할 만큼 표가 넓고 동시에 고정 열을 내줄 폭도 있어** 이득이 분명하다.
 * - 폰(<768)은 고정 열 2개가 가용 폭의 40%대를 먹는다(375px 기준). 남는 폭으로 데이터를 못 읽으면 고정한
 *   의미가 없다는 게 768 안의 논거 — **실제로 그런지 확인하려고 지금 0 으로 열어 둔 것**이다.
 */
export const STICKY_MIN_WIDTH = 0;

/** 고정 헤더를 켤 때 표에 씌우는 세로 상한. 고정 px 이 아니라 뷰포트 기준이라 화면 크기를 따라간다. */
const STICKY_MAX_HEIGHT = '70dvh';

/**
 * 고정 헤더/고정 열의 **공통 전제**를 한곳에서 판정한다 — 가상화 표 두 종(`VirtualSimpleTable`·
 * `VirtualGroupedTable`)이 같은 경계·같은 상한을 쓰게.
 *
 * **세로 상한이 왜 전제인가** — 열이 많아 가로 스크롤이 표 뷰포트에서 일어나면, CSS 명세상 한 축이 `visible`
 * 이 아닐 때 **반대축도 `visible` 로 못 남으므로** 그 뷰포트가 곧 sticky 의 기준 스크롤포트가 된다. 세로 상한이
 * 없으면 그 뷰포트가 세로로 안 넘쳐 `top: 0` 이 영영 발동하지 않는다(헤더가 본문과 같이 흘러간다).
 * 그래서 `stickyHeader` 는 취향 스위치가 아니라 **`maxHeight` 를 덮어쓰는** 옵션이다.
 *
 * 뷰포트 기준 상한이라 **짧은 결과는 상한에 안 닿아 내부 스크롤조차 안 생긴다**(고정 전과 동일하게 보인다).
 */
export function useStickyTable(
  stickyHeader: boolean,
  maxHeight: number | string | undefined,
): { canStick: boolean; boundedHeight: number | string | undefined } {
  const canStick = useMinWidth(STICKY_MIN_WIDTH);
  return {
    canStick,
    boundedHeight: stickyHeader && canStick ? STICKY_MAX_HEIGHT : maxHeight,
  };
}

/**
 * 인계 임계 — 표 상자가 이 비율만큼 보이면 내부 스크롤을 넘긴다. 1.0 은 픽셀 하나에도 걸려 전환이 급하고
 * 소수 픽셀 높이에서 영영 성립 안 할 수 있어 살짝 낮춘다.
 */
const TAKEOVER_RATIO = 0.98;

/**
 * **스크롤 인계** — 표 상자가 화면에 다 들어오기 전에는 내부 스크롤을 죽여 둔다.
 *
 * 세로 상한이 걸린 표는 페이지 안에 **또 하나의 스크롤 영역**이 된다. 그러면 표가 반쯤 걸쳐 있을 때 커서가
 * 어디 있느냐로 「페이지가 굴러갈지 표가 굴러갈지」가 갈려, 스크롤이 제멋대로인 것처럼 느껴진다. 그래서
 * **위치**로 정한다: 표가 온전히 보이기 전엔 페이지만, 온전히 보이면 그때부터 표가 받는다.
 *
 * ⚠️ `overflow-y: hidden` 으로 끄는 게 핵심 — **`hidden` 도 명세상 scroll container** 라 sticky 헤더·고정 열이
 * 그대로 산다(사용자만 못 굴린다). `max-height`·`display` 를 안 건드리니 레이아웃·가상화 계산도 불변이고,
 * 가로축(`overflow-x`)도 영향받지 않는다.
 *
 * ⚠️ 위치 계산이 아니라 **가끔 토글**이라 스크롤마다 도는 일이 없다 — JS 로 헤더를 따라 움직이던 방식과 달리
 * 떨림이 원천적으로 없다.
 *
 * @param bodyRef 표 안 아무 엘리먼트(뷰포트는 여기서 `closest` 로 찾는다).
 */
export function useScrollTakeover(bodyRef: RefObject<HTMLElement | null>, enabled: boolean): void {
  useLayoutEffect(() => {
    const viewport = bodyRef.current?.closest<HTMLElement>('[data-slot=scroll-area-viewport]');
    if (!enabled || !viewport) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        // 표가 화면보다 크면 비율 1 에 영영 못 닿는다 — 그 경우 인계를 걸면 **영영 못 굴린다**. 열어 둔다.
        const taller =
          entry.rootBounds != null && entry.boundingClientRect.height >= entry.rootBounds.height;
        const takeover = taller || entry.intersectionRatio >= TAKEOVER_RATIO;
        // `''` = 스타일시트 값(base-ui 의 `scroll`)으로 복귀. 명시값을 남기지 않는다.
        viewport.style.overflowY = takeover ? '' : 'hidden';
      },
      // 임계 전후로 콜백이 오게 세 지점을 준다(IO 는 **임계를 넘을 때만** 부른다).
      { threshold: [0, TAKEOVER_RATIO, 1] },
    );
    observer.observe(viewport);
    return () => {
      observer.disconnect();
      viewport.style.overflowY = '';
    };
  }, [bodyRef, enabled]);
}
